import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type { ProjectMethodologyEnum } from 'gql/graphql';
import {
    type CatalogOtherCostVariableItemDataFilterInput,
    type CatalogOtherCostVariableItemDataSortInput,
} from 'gql/graphql';
import type { SelectedFilters } from 'uikit';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { catalogOtherCostVariablesGql, getCatalogsNamesByFilter } from '../catalogQueries';

const getWhereVariables = (filters: SelectedFilters): CatalogOtherCostVariableItemDataFilterInput | undefined => {
    const predicates = Object.entries(filters)
        .filter(([, { selected, value }]) => selected.length && !R.isNil(value) && value.length)
        .map(([fieldName, { value }]) => ({
            [fieldName]: { in: value.map(({ data }: { data: { value: string } }) => data.value) },
        }));

    return predicates.length
        ? {
              and: predicates,
          }
        : undefined;
};

export const useCommonTableData = ({
    catalogId,
    methodology,
    globalOnly,
}: {
    catalogId: string | undefined;
    methodology: ProjectMethodologyEnum;
    globalOnly: boolean;
}) => {
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [currentPageSize, setCurrentPageSize] = useState<number>(100);
    const [queryWhere, setQueryWhere] = useState<SelectedFilters>({});
    const [queryOrder, setQueryOrder] = useState<CatalogOtherCostVariableItemDataSortInput[] | undefined>(undefined);

    const dispatch = useDispatch();

    const memoizedRequestIds = useMemo(
        () => [`catalogOtherCostVariables_${catalogId}`, `getCatalogsNamesByFilter_${catalogId}`],
        [catalogId],
    );

    const [catalogOtherCostVariablesReset] = useResetMultipleStoreData({
        requestIds: memoizedRequestIds,
    });

    useEffect(
        () => () => {
            catalogOtherCostVariablesReset();
        },
        [catalogOtherCostVariablesReset],
    );

    const catalogsVariables = useMemo(
        () => ({
            take: 1,
            where: { uniqueId: { in: [String(catalogId)] } },
        }),
        [catalogId],
    );

    const otherCostVariables = useMemo(
        () => ({
            skip: currentPage * currentPageSize,
            take: currentPageSize,
            catalogUniqueId: catalogId ?? '',
            where: getWhereVariables(queryWhere),
            order: queryOrder,
            globalOnly,
            methodology,
        }),
        [currentPage, currentPageSize, catalogId, queryOrder, queryWhere, globalOnly, methodology],
    );

    const [getCatalogsNamesByFilterData, getCatalogsNamesByFilterStatus] = useRequest({
        requestId: `getCatalogsNamesByFilter_${catalogId}`,
        query: getCatalogsNamesByFilter,
        variables: catalogsVariables,
    });

    const [
        catalogOtherCostVariablesData,
        catalogOtherCostVariablesStatus,
        catalogOtherCostVariablesError,
        catalogOtherCostVariablesRequest,
    ] = useRequest({
        requestId: `catalogOtherCostVariables_${catalogId}`,
        query: catalogOtherCostVariablesGql,
        variables: otherCostVariables,
    });

    const updateCatalogVariablesOther = useCallback(() => {
        dispatch(catalogOtherCostVariablesRequest(otherCostVariables));
    }, [catalogOtherCostVariablesRequest, dispatch, otherCostVariables]);

    const totalPages = useMemo(
        () => Math.ceil((catalogOtherCostVariablesData?.catalogOtherCostVariables?.totalCount ?? 0) / currentPageSize),
        [catalogOtherCostVariablesData?.catalogOtherCostVariables?.totalCount, currentPageSize],
    );

    const isError = useMemo(
        () => catalogOtherCostVariablesStatus === RequestStatus.ERROR,
        [catalogOtherCostVariablesStatus],
    );

    const isLoading = useMemo(
        () =>
            [getCatalogsNamesByFilterStatus, catalogOtherCostVariablesStatus].includes(RequestStatus.FETCHING) &&
            (!catalogOtherCostVariablesData || !getCatalogsNamesByFilterData?.catalogs?.items?.[0]?.name),
        [
            catalogOtherCostVariablesData,
            catalogOtherCostVariablesStatus,
            getCatalogsNamesByFilterData?.catalogs?.items,
            getCatalogsNamesByFilterStatus,
        ],
    );

    return useMemo(
        () => ({
            catalogName: getCatalogsNamesByFilterData?.catalogs?.items?.[0]?.name,
            getCatalogsNamesByFilterStatus,
            catalogOtherCostVariablesData: catalogOtherCostVariablesData?.catalogOtherCostVariables?.items,
            catalogOtherCostVariablesStatus,
            catalogOtherCostVariablesError,
            currentPage,
            setCurrentPage,
            currentPageSize,
            setCurrentPageSize,
            totalPages,
            totalCount: catalogOtherCostVariablesData?.catalogOtherCostVariables?.totalCount ?? 0,
            setQueryWhere,
            setQueryOrder,
            updateCatalogVariablesOther,
            isLoading,
            isError,
        }),
        [
            catalogOtherCostVariablesData?.catalogOtherCostVariables?.items,
            catalogOtherCostVariablesData?.catalogOtherCostVariables?.totalCount,
            catalogOtherCostVariablesError,
            catalogOtherCostVariablesStatus,
            currentPage,
            currentPageSize,
            getCatalogsNamesByFilterData?.catalogs?.items,
            getCatalogsNamesByFilterStatus,
            isError,
            isLoading,
            totalPages,
            updateCatalogVariablesOther,
        ],
    );
};
